import React, { useEffect, useMemo, useState } from "react";
import "../Auth.css";
import "./VerifyEmail.css";
import { AiOutlineMail } from "react-icons/ai";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";
import AuthLayout from "../../Components/AuthLayout";
import useAxiosHelper from "../../Common/AxiosHelper";
import { ApiPaths } from "../../Config/ApiPath";
import { toastFailed, toastSuccess } from "../../Config/BasicInfo";

const VERIFY_STORAGE_KEY = "hablink_email_verify";
const RESEND_COOLDOWN_SEC = 60;

const maskEmail = (email = "") => {
  const [local, domain] = String(email).split("@");
  if (!local || !domain) return email || "your email";
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"*".repeat(Math.max(local.length - 2, 3))}@${domain}`;
};

const readStoredIdentity = () => {
  try {
    const raw = sessionStorage.getItem(VERIFY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { AxiosPost } = useAxiosHelper();

  const identity = useMemo(() => {
    const fromState = location.state || {};
    const fromStore = readStoredIdentity();
    return {
      email: fromState.email || fromStore.email || "",
      username: fromState.username || fromStore.username || "",
      name: fromState.name || fromStore.name || "",
    };
  }, [location.state]);

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);

  useEffect(() => {
    if (identity.email || identity.username) {
      sessionStorage.setItem(VERIFY_STORAGE_KEY, JSON.stringify(identity));
    }
  }, [identity]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleOtpChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      toastFailed("Please enter the 6-digit OTP");
      return;
    }
    if (!identity.email && !identity.username) {
      toastFailed("Missing account details. Please register or login again.");
      navigate("/register");
      return;
    }

    setLoading(true);
    try {
      const body = {
        otp,
        action: "email_verification",
        ...(identity.email ? { email: identity.email } : {}),
        ...(identity.username ? { username: identity.username } : {}),
      };
      const res = await AxiosPost(ApiPaths.verifyEmail, body);
      toastSuccess(res?.message || "Email verified successfully");
      sessionStorage.removeItem(VERIFY_STORAGE_KEY);
      navigate("/login", {
        state: { username: identity.username || "" },
      });
    } catch (error) {
      toastFailed(
        error?.response?.data?.message || "Invalid or expired OTP. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    if (!identity.email && !identity.username) {
      toastFailed("Missing account details. Please register again.");
      navigate("/register");
      return;
    }

    setLoading(true);
    try {
      const body = {
        ...(identity.email ? { email: identity.email } : {}),
        ...(identity.username ? { username: identity.username } : {}),
      };
      const res = await AxiosPost(ApiPaths.resendVerificationEmail, body);
      if (res?.alreadyVerified) {
        toastSuccess(res?.message || "Email already verified");
        navigate("/login");
        return;
      }
      toastSuccess(res?.message || "OTP sent successfully");
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (error) {
      toastFailed(
        error?.response?.data?.message || "Failed to resend OTP. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? <Loader /> : null}
      <AuthLayout
        title="Verify your email"
        subtitle={`Enter the 6-digit OTP sent to ${maskEmail(identity.email)}`}
        footerText="Already verified?"
        footerLinkText="Sign In"
        footerTo="/login"
      >
        <div className="authForm verifyEmailForm">
          <div className="verifyEmailHint">
            <i>
              <AiOutlineMail />
            </i>
            <p>
              We sent a verification code to your inbox. It expires in 10
              minutes.
            </p>
          </div>

          <div className="authField">
            <label className="fieldLabel">OTP Code</label>
            <div className="fieldBox">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={handleOtpChange}
                className="verifyOtpInput"
                maxLength={6}
              />
            </div>
          </div>

          <button type="button" className="authBtn" onClick={verifyOtp}>
            Verify Email
          </button>

          <div className="verifyResendRow">
            <span>Didn&apos;t get the code?</span>
            <button
              type="button"
              className="verifyResendBtn"
              onClick={resendOtp}
              disabled={cooldown > 0}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
            </button>
          </div>

          <p className="verifyBackLink">
            Wrong email? <Link to="/register">Register again</Link>
          </p>
        </div>
      </AuthLayout>
    </>
  );
};

export default VerifyEmail;
